import {
  AccountName,
  ActiveTradeNextOrder,
  ApiGeneralDataResponse,
  CoinExtraExchangesData,
  CoinName,
  ConfigurationItem,
  ExchangeName,
  Limits,
  RawChartData,
  TimeRange,
  Direction,
  TradeSourceType,
  SingleValueChartData,
  FiltersRequest,
  BaseLimit,
  BotType,
  FormTrailingStopData,
  FixMeLater,
  AmountType,
} from "@shared/types";
import {
  CreateTradeOrder,
  TradeSource,
} from "@shared/features/trades/common/types";
import { TradeTypes } from "@shared/features/tradingTerminal/common/types";

export interface ExistingQuantityOrderData {
  is_existing_coins: true;
  quantity: number;
  amount: number;
  price: number;
  ratio: number;
}

export enum TradeOrderType {
  SELL_LIMIT = 1,
  SELL_STOP = 2,
  SELL_MARKET = 3,
  BUY_LIMIT = 4,
  BUY_STOP = 5,
  BUY_MARKET = 6,
  TRAILING_STOP_SELL = 7,
  TRAILING_STOP_BUY = 8,
}

export enum OrderTypes {
  ENTRY = "entry",
  TAKE_PROFIT = "takeProfit",
  STOP = "stop",
}

export interface TradeNextOrders {
  next_entry?: ActiveTradeNextOrder;
  next_stop?: ActiveTradeNextOrder;
  next_take_profit?: ActiveTradeNextOrder;
}

export interface TradeData extends TradeNextOrders {
  id: number;
  symbol: string;
  price: number;
  allocated_amount: number;
  invested_amount: number;
  allocated_quantity: number;
  invested_quantity: number;
  allocated_amount_btc: number;
  invested_amount_btc: number;
  allocated_amount_usd: number;
  invested_amount_usd: number;
  graph_data: TradeGraphData | GridTradeGraphData;
  exchange: string;
  account: string;
  group: string;
  risk_reward: string;
  potential_profit: string;
  usd_amount_change: number;
  btc_amount_change: number;
  timestamp: number;
  open_timestamp: number;
  close_timestamp: number;
  "Long/Short": Direction;
  trade_type: string;
  trade_status: string;
  entry_price: number;
  closest_order?: ClosestOrder;
  margin_type: string;
  leverage: number;
  source_type: TradeSourceType;
  potential_profit_usd: number;
  potential_profit_btc: number;
  close_reason: string;
  price_when_closed: number;
  price_graph_data?: SingleValueChartData;
  status_value?: TradeStatus;
  client_is_valid: boolean;
  client_id: number;
  bot_id: number;
  signal_id: number;
  /**
   * Optional: every order of the trade as a price ladder (entries,
   * take-profits and the stop, in execution order). Not sent yet; when it is,
   * the new look's progress column draws the full ladder instead of the
   * simplified one built from `next_*` and `graph_data`.
   */
  levels?: TradeLadderLevel[];
  /** Optional, grid trades: completed buy → sell cycles (not sent yet). */
  grid_cycles?: number;
}

/** One order of a trade's price ladder. */
export interface TradeLadderLevel {
  kind: "entry" | "take_profit" | "stop";
  price: number;
  /** Filled share of the order, 0–1. */
  filled: number;
}

export type TradeDataWithKey = TradeData & {
  key: string | number;
};

export type SocketTradeData = TradeData;

export interface TradeExtraInfoOrdersGraphData {
  graph_data: RawChartData;
  price_coin: CoinName;
}

export interface OrderCardData {
  id: number;
  number: number;
  status: OrderStatus;
  reason: string;
  quantity: number;
  filled_quantity: number;
  amount: number;
  filled_amount: number;
  target_price: number;
  ratio: number;
  display_ratio: number;
  ratio_filled: number;
  order_extra_data?: [string, string][];
  should_be_active?: boolean;
  reason_link?: string;
  type?: OrderTypes;
}

export type TradeExtraInfoGeneralMapData = Record<
  string,
  string | number | null
> & {
  Account: AccountName;
  "Allocated Amount": number;
  "Current Price": number;
  "Entry Price": number;
  Exchange: ExchangeName;
  "Filled Quantity": number;
  Group: string;
  Leverage: string;
  "Long/Short": string;
  "Opened at": number;
  "Potential Profit": string;
  "Risk/Reward": string;
  Status: string;
  "Trade type": string;
  Source: string;
  "Margin Type": string;
  "Estimated Liquidation Price"?: number;
};

export interface TradeExtraInfoGeneralData {
  trade_status: string;
  graph_data: TradeGraphData | GridTradeGraphData;
  general: TradeExtraInfoGeneralMapData;
}

export interface NumberOfTargetsLimits {
  regular: number;
  stop: number;
  breakout_entries: number;
  regular_entries: number;
}

export interface CreateTradeLimits extends Limits {
  number_of_targets: NumberOfTargetsLimits;
  max_trades?: number;
  can_open_both_directions?: boolean;
  leverage?: { min_multiplier: number; margin_type: number };
}

export interface Option<T = string | boolean | number> {
  value: T;
  name: string;
}

export interface GeneralOrdersData {
  buying_strategy: { data: any[]; name: any[] };
  selling_strategy: { data: any[]; name: any[] };
  current_price: number;
  defaults: {
    default_stop_percent: number;
    number_of_entry_targets: number;
    number_of_take_profit_targets: number;
  };
}

export interface CreateTradeGeneralInfo {
  long_trades: number;
  short_trades: number;
  cross_multiplier: boolean;
  is_contracts: boolean;
  amount_coin: CoinName;
}

export interface CreateTradeData {
  general: {
    options: NameAndOptions[];
    info: CreateTradeGeneralInfo;
  };
  id_to_value: { [key: number]: number };
  formulas: {
    risk_percent?: string;
  };
  configuration: ConfigurationItem;
  orders: GeneralOrdersData;
}

export interface SymbolClientsData {
  id: number;
  exchange: ExchangeName;
  name: AccountName;
  can_trade: boolean;
}

export interface GetSymbolsData {
  active_symbols: string[];
  inactive_symbols: string[];
}

export interface GetTradesRequest {
  exchange?: string;
  account_id?: number;
  coin?: string;
  dca_bot_id?: number;
  include_closest_pending_order?: boolean;
  add_extra_info?: boolean;
  add_price_graph_data?: boolean;
  signal_id?: number;
  filters?: FiltersRequest;
  should_limit?: boolean;
  is_backtesting?: boolean;
}

export interface GetClientsDataFromSymbolRequest {
  symbol: string;
}

export interface BaseCreateTradeData {
  leverage: number;
  margin_type: MarginTypes;
  reverse: boolean;
  symbol: CoinName;
  trade_type: TradeTypes.REGULAR | TradeTypes.BREAKOUT;
}

export interface CreateTradeRequest extends CreateTradeGeneralData {
  account_id: number;
  buy_section: (CreateTradeOrder | ExistingQuantityOrderData)[];
  sell_section: CreateTradeOrder[];
  stop_section: CreateTradeOrder[];
  configuration?: FixMeLater;
  trailing_entry?: number | null;
  trailing_take_profit?: number | null;
  trailing_stop?: FormTrailingStopData | null;
}

export type BaseCreateTradeRequest = Omit<
  CreateTradeRequest,
  "account_id" | "trade_type"
>;

export type CreateSignalRequest = BaseCreateTradeRequest & {
  signal_type: TradeTypes.REGULAR | TradeTypes.BREAKOUT;
  amount_value: number | null;
  amount_type: AmountType | null;
  sub_group_ids: number[];
  exchanges: string[];
};

export interface EditTradeRequest {
  trade_id: number;
  buy_section: EditTradeTarget[];
  sell_section: EditTradeTarget[];
  stop_section: EditTradeTarget[];
}

export interface GetTradeExtraInfoOrdersGraphBySymbolAndExchange {
  time_range: TimeRange;
  symbol: CoinName;
  exchange: ExchangeName;
}

export interface BaseGetTradeExtraInfoDataRequest {
  ignore_status: boolean;
  trade_id: number;
  is_backtesting?: boolean;
}

export interface GetTradeExtraInfoOrdersGraphByTrade
  extends BaseGetTradeExtraInfoDataRequest {
  time_range: TimeRange;
}

export type GetTradeExtraInfoOrdersGraphRequest =
  | GetTradeExtraInfoOrdersGraphBySymbolAndExchange
  | GetTradeExtraInfoOrdersGraphByTrade;

export interface TradeGraphData {
  entry_progress: number;
  take_profit_progress: number;
  profit: number;
  time_passed: string;
}

export interface GridTradeGraphData {
  time_passed: string;
  min_price: number;
  max_price: number;
  current_price: number;
  entry_progress: number; // TODO: REMOVE!
}

export type GetTradeExtraInfoGeneralResponse =
  ApiGeneralDataResponse<TradeExtraInfoGeneralData> & {
    trade_open: boolean;
  };

export interface TradeMetadata {
  exchange: string;
  source: TradeSource;
  symbol: string;
  trade_open: boolean;
  open_time: number;
  close_time?: number;
  client_is_valid: boolean;
  client_id: number;
  is_automated?: boolean;
}

export interface ClosestOrder {
  is_hot?: boolean;
  order_type: OrderTypes;
  price: number;
  ratio: number;
}

export enum OrderStatus {
  FULFILLED = "Fulfilled",
  PENDING = "Pending",
  PARTIALLY_FULFILLED = "Partially fulfilled",
  CANCELLED = "Cancelled",
}

export type RawOrder = (number | string)[];

export interface RawTradeExtraInfoOrdersData {
  account_id: number;
  amount_coin: CoinName;
  breakout: boolean;
  current_price: number;
  is_contracts: boolean;
  leverage: number;
  price_coin: string;
  reverse: boolean;
  show_quantity_in_edit: boolean;
  symbol: CoinName;
  exchange: string;
  entry_order_cards: RawOrder[];
  stop_order_cards: RawOrder[];
  take_profit_order_cards: RawOrder[];
  number_of_active_entries?: number;
  source: TradeSource;
}

export type TradeExtraInfoOrdersData = Omit<
  RawTradeExtraInfoOrdersData,
  "entry_order_cards" | "stop_order_cards" | "take_profit_order_cards"
> & {
  entry_order_cards: OrderCardData[];
  stop_order_cards: OrderCardData[];
  take_profit_order_cards: OrderCardData[];
};

interface BaseGridOrderData {
  id: number;
  type: TradeOrderType;
  price: number;
}

export interface GridCardData {
  id: number;
  number: number;
  price: number;
  ratio: number;
  quantity: number;
  amount: number;
  num_of_filled_take_profits: number;
  realised_pnl_dict?: { BTC: number; USD: number };
  last_order_dict?: BaseGridOrderData & { time: number };
  pending_order_dict?: BaseGridOrderData;
}

interface GridTradeNextOrder {
  price: number;
  distance: number;
  grid_number: number;
}

export interface TradeExtraInfoStats {
  id: number;
  entry_price: number;
  main_coin: string;
  change: {
    USD: number;
    BTC: number;
    percent: number;
  };
  entries_filled: {
    amount: {
      USD: number;
      BTC: number;
    };
    number: number;
    total: number;
  };
  last_entry: {
    target_number: number;
    number_of_targets: number;
    activity_time: number;
    time_passed: string;
  };
  next_entry: GridTradeNextOrder;
  next_take_profit: GridTradeNextOrder;
  take_profits_filled: number;
  realised_pnl: [amount: number, ratio: number];
  days_running: number;
  trade_open: boolean;
  bot_type: BotType;
}

export type NameAndOptions = [string, Option[]] | [string, Option[], number];

export type GetCreateTradeGeneralDataRequest = {
  account_id: number;
  symbol: string;
  include_without?: boolean;
};

export type GetCreateTradeGeneralDataResponse = ApiGeneralDataResponse<
  CreateTradeData,
  CreateTradeLimits
>;

export type CreateSignalData = Omit<CreateTradeData, "orders"> & {
  targets: GeneralOrdersData;
  trailing_stop?: FormTrailingStopData | null;
};

export type GetSignalCreateGeneralDataResponse = ApiGeneralDataResponse<
  CreateSignalData,
  CreateTradeLimits
>;

export type GetCreateSignalOrTradeGeneralDataResponse =
  | GetCreateTradeGeneralDataResponse
  | GetSignalCreateGeneralDataResponse;

export type RawGetTradeExtraInfoOrdersResponse =
  ApiGeneralDataResponse<RawTradeExtraInfoOrdersData>;
export type GetTradeExtraInfoOrdersResponse = ApiGeneralDataResponse<
  TradeExtraInfoOrdersData,
  CreateTradeLimits
>;

export enum LongShort {
  LONG = 1,
  SHORT = 2,
}

export enum MarginTypes {
  ISOLATED = 1,
  CROSS = 2,
}

type RawTradeExtraInfoActivitiesData = (string | number)[][];
export type TradeExtraInfoActivity = {
  relevant_time: number;
  description: string;
  status: string;
  execution_price: number;
  activity_component: number;
  order_type?: TradeOrderType;
  grid_id?: number;
};

export interface TradesGeneralDataResponse<T>
  extends ApiGeneralDataResponse<T> {
  aggregated_data?: {
    number_of_closed_trades: number;
    change: { BTC: number; USD: number };
  };
}

export type GetTradesDataResponse =
  TradesGeneralDataResponse<CoinExtraExchangesData>;
export type GetSymbolsResponse = ApiGeneralDataResponse<GetSymbolsData>;

export interface CreateTradeGeneralData extends BaseCreateTradeData {
  cancel_on_first_target_reached: boolean;
}

export interface FreeTextTradeData extends BaseCreateTradeData {
  buy_section: number[];
  buy_zone: [number, number];
  sell_section: number[];
  sell_zone: [number, number];
  stop_section: number;
}

export type EditTradeTarget = [
  number | undefined, // order_id
  number, // price
  number, // ratio
  boolean, // is_closed
  number, // target_number
  number, // amount
  number, // amount_filled
  number, // quantity
  boolean, // is_existing
  number // ratio_filled
];

export type GetTradeExtraInfoOrdersGraphResponse =
  ApiGeneralDataResponse<TradeExtraInfoOrdersGraphData>;
export type GetTradeExtraInfoConfigurationResponse =
  ApiGeneralDataResponse<ConfigurationItem>;
export type GetTradeExtraInfoActivitiesResponse =
  ApiGeneralDataResponse<RawTradeExtraInfoActivitiesData>;

export interface RawTradeDetailsSocketData {
  stats?: ApiGeneralDataResponse<TradeExtraInfoStats>;
  metadata?: ApiGeneralDataResponse<TradeMetadata>;
  general?: GetTradeExtraInfoGeneralResponse;
  orders?: RawGetTradeExtraInfoOrdersResponse;
  activities?: GetTradeExtraInfoActivitiesResponse;
  configuration?: GetTradeExtraInfoConfigurationResponse;
  grids?: { data: any[][]; headers: string[] };
}

export interface TradeDetailsSocketWrapper {
  trade_id: number;
  original_trade_id: number;
  data: RawTradeDetailsSocketData;
}

export interface TradeDetailsSocketWrapperProcessed {
  trade_id: number;
  original_trade_id: number;
  data: TradeDetailsSocketData;
}

export interface TradeDetailsSocketData {
  stats?: TradeExtraInfoStats;
  metadata?: TradeMetadata;
  general?: GetTradeExtraInfoGeneralResponse;
  orders?: GetTradeExtraInfoOrdersResponse;
  activities?: TradeExtraInfoActivity[];
  configuration?: GetTradeExtraInfoConfigurationResponse;
  grids?: GridCardData[];
}

export enum TradeStatus {
  BUYING_STAGE = 1,
  SELLING_STAGE = 2,
  CANCELLED = 3,
  FULFILLED = 4,
  STOPPED_OUT = 5,
  PENDING = 6,
  INACTIVE = 7,
  PARTIAL_FULFILLED = 8,
  EDITED = 9,
  IN_EDIT = 10,
  LIQUIDATED = 11,
  POSITION_CLOSED = 12,
  SUBSCRIPTION_ENDED = 13,
  MARKET_IS_NOT_AVAILABLE = 14,
  CLIENT_DELETED = 15,
  IN_CANCEL = 16,
}

export interface BaseTradeActionRequest {
  trade_id: number;
  trade_open?: boolean;
  ignore_status?: boolean;
}

export interface SellPartialTradeRequest extends BaseTradeActionRequest {
  quantity: number;
}

export interface MoveEntryToCurrentPriceRequest extends BaseTradeActionRequest {
  order_id: number;
}

export interface TradeFirstEntry {
  id: number;
  price: number;
}

export interface TradeQuantityLimit {
  amount: BaseLimit;
}

export enum TradeActionType {
  TRADE_DETAILS,
  CLOSE_TRADE,
  EDIT_ORDERS,
  EDIT_CONFIGURATION,
  MOVE_NEAREST_ENTRY,
  SELL_PARTIALLY,
  SHARE_RESULTS,
}

export type GetTradesGraphsDataRequest = {
  time_range: TimeRange;
  time_aggregation?: TimeRange;
  filters?: FiltersRequest;
};

export enum TradeGraphType {
  PROFIT_BY_DAY = 1,
  ACCUMULATING_PROFIT = 2,
}
